import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLiveMarkets } from "@/lib/useLiveMarkets";
import { AssetGlyph, ChangePill, fmtPrice } from "@/components/market/PriceCell";
import { Notice, Segmented } from "@/components/paisa/ui";
import DepositModal from "@/components/wallet/DepositModal";
import WithdrawModal from "@/components/wallet/WithdrawModal";
import TransferModal from "@/components/wallet/TransferModal";
import WhitelistManager from "@/components/wallet/WhitelistManager";
import HistoryTable from "@/components/wallet/HistoryTable";
import ChainStatus from "@/components/wallet/ChainStatus";

const DISPLAY_CURRENCY_KEY = "cm_display_currency";
const TABS = [
  { value: "balances", label: "Balances" },
  { value: "history", label: "History" },
];
const GRID = "grid-cols-[1.4fr_1fr_1fr_110px_1fr]";

const amt = (v, d = 8) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: Math.min(d, 8) });

function useCountUp(target) {
  const [shown, setShown] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (done || !target) return undefined;
    let f = 0;
    const id = setInterval(() => {
      f += 1;
      setShown(target * (1 - Math.pow(1 - f / 40, 3)));
      if (f >= 40) {
        clearInterval(id);
        setDone(true);
      }
    }, 16);
    return () => clearInterval(id);
  }, [target, done]);
  return done ? target : shown;
}

function useChanges() {
  const { rows } = useLiveMarkets("USDT");
  return useMemo(() => {
    const next = { USDT: 0 };
    rows.forEach((r) => (next[r.symbol] = Number(r.change_pct_24h)));
    return next;
  }, [rows]);
}

export default function WalletsPage() {
  const [params] = useSearchParams();
  const [balances, setBalances] = useState(null);
  const [totalUsd, setTotalUsd] = useState(0);
  const [totalInr, setTotalInr] = useState(0);
  const [rate, setRate] = useState(0);
  const [currency, setCurrency] = useState(() => {
    try {
      return localStorage.getItem(DISPLAY_CURRENCY_KEY) || "USD";
    } catch {
      return "USD";
    }
  });
  const [tab, setTab] = useState("balances");
  const [hideZero, setHideZero] = useState(false);
  const [hideValues, setHideValues] = useState(false);
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [openModal, setOpenModal] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const changes = useChanges();
  const fxLive = useRef(false);

  const load = async () => {
    setRefreshing(true);
    setLoadError("");
    try {
      const r = await api.get("/api/wallet/balances");
      setBalances(r.data.balances);
      setTotalUsd(Number(r.data.total_usd || 0));
      setTotalInr(Number(r.data.total_inr || 0));
      if (r.data.usdt_inr_rate && !fxLive.current) setRate(Number(r.data.usdt_inr_rate));
    } catch (e) {
      setLoadError(e.response?.data?.detail || e.message || "Failed to load wallet");
    } finally {
      setRefreshing(false);
    }
  };

  const { usdtInr } = useLiveMarkets("USDT");
  useEffect(() => {
    const v = Number(usdtInr);
    if (v > 0) {
      fxLive.current = true;
      setRate(v);
    }
  }, [usdtInr]);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const a = params.get("action");
    if (["deposit", "withdraw", "transfer", "whitelist"].includes(a)) {
      setOpenModal({ type: a, asset: params.get("asset") || undefined });
    }
    if (params.get("tab") === "history") setTab("history");
  }, [params]);

  const toggleCurrency = () =>
    setCurrency((c) => {
      const next = c === "USD" ? "INR" : "USD";
      try {
        localStorage.setItem(DISPLAY_CURRENCY_KEY, next);
      } catch {
        /* storage unavailable */
      }
      return next;
    });

  const inr = rate ? totalUsd * rate : totalInr;
  const shown = useCountUp(currency === "INR" ? inr : totalUsd);
  const money = (usd) =>
    currency === "INR"
      ? `₹${Number(usd * rate).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
      : usd
        ? `$${fmtPrice(usd)}`
        : "$0.00";
  const mask = (s) => (hideValues ? "••••" : s);

  const visible = useMemo(() => {
    if (!balances) return [];
    const q = search.trim().toLowerCase();
    return balances.filter((b) => {
      if (hideZero && Number(b.total) === 0) return false;
      if (q && !b.asset.toLowerCase().includes(q) && !String(b.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [balances, hideZero, search]);

  const hasHeld = useMemo(() => (balances || []).some((b) => Number(b.held || 0) > 0), [balances]);

  const onModalClose = (didMutate) => {
    setOpenModal(null);
    if (didMutate) {
      load();
      setHistoryKey((k) => k + 1);
    }
  };

  const outlineBtn = "rounded-lg border border-border bg-surface-2 px-4 py-2 text-sm transition hover:border-primary";

  return (
    <AppShell bare>
      <div className="page-fade mx-auto max-w-7xl px-4 py-10">
        <section className="glass-panel overflow-hidden rounded-2xl" data-testid="wallet-total-card">
          <div className="relative flex flex-col gap-6 p-7 sm:flex-row sm:items-end sm:justify-between">
            <div className="relative">
              <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="wallet-page-title">
                Total portfolio value
                <button
                  type="button"
                  onClick={toggleCurrency}
                  data-testid="wallet-currency-toggle"
                  className="rounded-md border border-border bg-surface-2 px-1.5 py-0.5 text-[10px] text-foreground transition hover:border-primary"
                >
                  {currency}
                </button>
                <button
                  type="button"
                  onClick={() => setHideValues((v) => !v)}
                  data-testid="wallet-toggle-values"
                  className="text-[11px] underline-offset-2 hover:text-foreground hover:underline"
                >
                  {hideValues ? "Show" : "Hide"}
                </button>
              </p>
              <p className="num mt-2 text-4xl font-semibold" data-testid="wallet-total-usd">
                {hideValues ? "••••••" : currency === "INR" ? `₹${shown.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : `$${fmtPrice(shown)}`}
              </p>
              <p className="num mt-1 text-sm text-muted-foreground">
                {currency === "INR" ? `≈ ${mask(`$${fmtPrice(totalUsd)}`)}` : `≈ ${mask(`₹${inr.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`)}`}
                {rate ? ` · 1 USDT ≈ ₹${rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : null}
              </p>
            </div>
            <div className="relative flex flex-col items-start gap-4 sm:items-end">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setOpenModal({ type: "deposit" })}
                  data-testid="wallet-action-deposit"
                  className="brand-gradient rounded-lg px-4 py-2 text-sm font-semibold text-primary-foreground"
                >
                  Deposit
                </button>
                <button type="button" onClick={() => setOpenModal({ type: "withdraw" })} data-testid="wallet-action-withdraw" className={outlineBtn}>
                  Withdraw
                </button>
                <button type="button" onClick={() => setOpenModal({ type: "transfer" })} data-testid="wallet-action-transfer" className={outlineBtn}>
                  Send
                </button>
                <button type="button" onClick={() => setOpenModal({ type: "whitelist" })} data-testid="wallet-action-whitelist" className={outlineBtn}>
                  Whitelist
                </button>
              </div>
              <button
                type="button"
                onClick={load}
                disabled={refreshing}
                data-testid="wallet-refresh"
                className="text-xs text-muted-foreground transition hover:text-foreground disabled:opacity-50"
              >
                {refreshing ? "Refreshing…" : "Refresh balances"}
              </button>
            </div>
          </div>
        </section>

        {loadError && (
          <Notice tone="error" className="mt-4 flex items-center justify-between gap-3" data-testid="wallet-load-error">
            <span>Couldn&apos;t refresh wallet: {loadError}</span>
            <button type="button" onClick={load} className="text-xs underline">
              Retry
            </button>
          </Notice>
        )}
        {hasHeld && (
          <Notice tone="warn" className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span data-testid="wallet-held-funds-banner">
              Some deposits are held because their source addresses aren&apos;t whitelisted. Whitelist them to release the funds.
            </span>
            <button
              type="button"
              onClick={() => setOpenModal({ type: "whitelist" })}
              className="rounded-md border border-warning/50 px-2.5 py-1 text-xs transition hover:bg-warning/10"
            >
              Manage whitelist
            </button>
          </Notice>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="sm:w-fit">
            <Segmented options={TABS} value={tab} onChange={setTab} testIdPrefix="wallet-tab" />
          </div>
          {tab === "balances" && (
            <div className="flex items-center gap-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search asset"
                data-testid="wallet-search"
                className="w-full rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-xs outline-none focus:border-primary sm:w-48"
              />
              <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} data-testid="wallet-hide-zero" className="accent-primary" />
                Hide zero
              </label>
            </div>
          )}
        </div>

        {tab === "balances" ? (
          <>
            <div className="mt-3">
              <p className="num text-sm text-muted-foreground">
                Wallet balance {mask(money(totalUsd))} · {balances?.length || 0} assets
              </p>
            </div>

            <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-surface/40">
              <div className="min-w-[760px]">
                <div className={`table-head grid ${GRID} gap-3 px-4 py-2.5`}>
                  <span>Asset</span>
                  <span className="text-right">Balance</span>
                  <span className="text-right">Value</span>
                  <span className="text-right">24h</span>
                  <span className="text-right">Actions</span>
                </div>
                {balances === null ? (
                  <div className="space-y-2 p-4">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="skeleton h-10 w-full" />
                    ))}
                  </div>
                ) : visible.length === 0 ? (
                  <p className="px-4 py-14 text-center text-sm text-muted-foreground">
                    {balances.length === 0 ? "Your wallet is empty. Deposit an asset to start trading on P2P." : "No assets match your filter."}
                  </p>
                ) : (
                  visible.map((a) => {
                    const total = Number(a.total || 0);
                    const usd = Number(a.usd_value || 0);
                    const unit = total > 0 ? usd / total : null;
                    const reserved = Number(a.held || 0) + Number(a.locked || 0);
                    return (
                      <div key={a.asset} data-testid={`wallet-row-${a.asset}`} className={`terminal-row ${GRID} gap-3 px-4 py-3`}>
                        <div className="flex min-w-0 items-center gap-3">
                          <AssetGlyph symbol={a.asset} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium">
                              {a.name || a.asset}
                              <span className="ml-1.5 text-xs text-muted-foreground">{a.asset}</span>
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {a.is_fiat ? "Fiat" : (a.networks || []).join(" · ")}
                              {unit != null && !a.is_fiat && <span className="num"> · ${fmtPrice(unit)}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="num text-right text-sm">
                          <span data-testid={`wallet-total-${a.asset}`}>{mask(amt(total, a.decimals))}</span>
                          <p className="text-[11px] text-muted-foreground">
                            <span data-testid={`wallet-available-${a.asset}`}>{mask(amt(a.available, a.decimals))}</span> free
                            {reserved > 0 && (
                              <span title="Held: deposits awaiting address whitelisting. Locked: reserved in open orders.">
                                {" "}
                                · <span data-testid={`wallet-held-${a.asset}`}>{mask(amt(a.held, a.decimals))}</span> held ·{" "}
                                <span data-testid={`wallet-locked-${a.asset}`}>{mask(amt(a.locked, a.decimals))}</span> locked
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="num text-right text-sm">{mask(money(usd))}</div>
                        <div className="flex justify-end">
                          {Number.isFinite(changes[a.asset]) && !a.is_fiat ? <ChangePill value={changes[a.asset]} /> : <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                        <div className="flex justify-end gap-2">
                          {a.is_fiat ? (
                            <span className="text-xs text-muted-foreground">Via P2P</span>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => setOpenModal({ type: "deposit", asset: a.asset })}
                                data-testid={`wallet-row-deposit-${a.asset}`}
                                className="rounded-md border border-border px-2.5 py-1 text-xs transition hover:border-primary"
                              >
                                Deposit
                              </button>
                              <button
                                type="button"
                                onClick={() => setOpenModal({ type: "withdraw", asset: a.asset })}
                                data-testid={`wallet-row-withdraw-${a.asset}`}
                                className="rounded-md border border-border px-2.5 py-1 text-xs transition hover:border-primary"
                              >
                                Withdraw
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex justify-end">
              <ChainStatus />
            </div>
            <HistoryTable key={historyKey} />
          </div>
        )}
      </div>

      {openModal?.type === "deposit" && <DepositModal initialAsset={openModal.asset} onClose={onModalClose} />}
      {openModal?.type === "withdraw" && <WithdrawModal initialAsset={openModal.asset} onClose={onModalClose} />}
      {openModal?.type === "transfer" && <TransferModal initialAsset={openModal.asset} onClose={onModalClose} />}
      {openModal?.type === "whitelist" && <WhitelistManager onClose={onModalClose} />}
    </AppShell>
  );
}
